import { getVersion, getEntryComponents, getLudsComponents } from './macros' with { type: 'macro' };
import { ModalService } from './services/luds-modal-service';
import { ToastService } from './services/luds-toast-service';

/** List of all the components */
const TAGS: Array<string> = [...getEntryComponents(), ...getLudsComponents()];

const isStaging = /luptest.com/.test(window.location.origin);
const isLocal = /lupdev.com/.test(window.location.origin);

/** Internal logging function */
function log(...args: unknown[]): void {
  const isLoggingEnabled = isStaging || isLocal;
  if (isLoggingEnabled) {
    // eslint-disable-next-line no-console
    console.log('[LuAnywhere]', ...args);
  }
}
/** Debounce timer for autoload to prevent duplicate calls */
let autoloadDebounceTimer: number | null = null;
/**
 * Load modules given a list of tags
 *
 * @example
 * LuAnywhere.loadModule(["lua-course-card", "lua-course-player"])
 */
export async function loadModule(pkgs: string[] = []): Promise<void> {
  await Promise.all(
    pkgs.map((pkg) => {
      if (pkg.startsWith('luds')) {
        const name = pkg.replace('luds-', '');
        return import(`./design-system/${name}/${name}.ts`);
      }

      if (pkg.startsWith('lua')) {
        const name = pkg.replace('lua-', '');
        return import(`./entry-components/${name}/${name}.ts`);
      }
    })
  );
}

/**
 * Scan page to find any entry-component tag, then load the respective module.
 * When the script is loaded, autoload is executed automatically
 *
 * @example
 * LuAnywhere.autoload()
 */
export async function autoload(): Promise<void> {
  const tags = document.querySelectorAll(TAGS.join(', '));
  const tagNames = [...tags].map((node) => node.tagName.toLowerCase());
  await loadModule(tagNames);

  log('Autoloaded components:', tagNames);

  dispatchEvent(
    new CustomEvent('lua-ready', {
      detail: {
        api: Object.freeze(LuAnywhere),
        tagNames,
      },
    })
  );
}

/**
 * Debounced version of autoload to prevent multiple simultaneous calls
 * Uses requestAnimationFrame to ensure DOM is ready
 */
function debouncedAutoload(): void {
  if (autoloadDebounceTimer !== null) {
    cancelAnimationFrame(autoloadDebounceTimer);
  }
  autoloadDebounceTimer = requestAnimationFrame(() => {
    autoload();
    autoloadDebounceTimer = null;
  });
}

/**
 * Load all the design system components
 *
 * @example
 * LuAnywhere.loadLuds()
 */
export async function loadLuds(exclude: string[] = [], loadModuleFn: typeof loadModule = loadModule): Promise<void> {
  await loadModuleFn(TAGS.filter((tag) => tag.startsWith('luds') && !exclude.includes(tag)));
  log('LUDS components loaded');
}

/** Public LuAnywhere API exposed to window.LuAnywhere */
export const LuAnywhere = {
  loadModule,
  autoload: debouncedAutoload,
  loadLuds,
  version: getVersion(),
  services: {
    modal: ModalService,
    toast: ToastService,
  },
  mode: import.meta.env.MODE,
};

/** Set internal api "hidden" into prototype of LuAnywhere */
Object.setPrototypeOf(LuAnywhere, {
  /**
   * This method is used to set the preload resource for the entry components
   */
  _setPreloadResource() {
    // NOTE: This function does not use variables to avoid mangling by minifiers.
    // Because we need to have a deterministic way to generate an integrity hash,
    // which is checked in the StorageFactory in the MonoRails.
    // eslint-disable-next-line prefer-rest-params
    if (typeof arguments[0] === 'object' && arguments[0] !== null && arguments[0].constructor === Object) {
      // eslint-disable-next-line prefer-rest-params
      dispatchEvent(new CustomEvent('lua-preload-resource', { detail: arguments[0] }));
    }
  },
});

(function (_global) {
  // Preload all the LUDS components
  loadLuds();

  // Wait for DOM to be ready, then scan for and load entry components
  document.addEventListener('DOMContentLoaded', () => {
    debouncedAutoload();
  });

  // Fallback if script loads after DOMContentLoaded has already fired
  window.addEventListener('load', () => {
    debouncedAutoload();
  });

  // Hash-based routing navigation (e.g., #/page)
  window.addEventListener('hashchange', () => {
    debouncedAutoload();
  });

  // Browser back/forward button navigation
  window.addEventListener('popstate', () => {
    debouncedAutoload();
  });

  // Modern Navigation API support (if available)
  if ('navigation' in window && window.navigation) {
    (window.navigation as EventTarget).addEventListener('navigatesuccess', () => {
      debouncedAutoload();
    });
  }

  // Expose LuAnywhere api to global scope
  _global.LuAnywhere = Object.freeze(LuAnywhere);
})(window);

declare global {
  interface Window {
    LuAnywhere: typeof LuAnywhere;
  }
}
